/**
 * Auth error rendering.
 *
 * A single component - `<AuthErrorBanner code={...} />` - is the only
 * thing surfaces need to render an auth-flow error. Callers pull the
 * short error identifier from whichever SDK they used:
 *
 *   - better-auth client responses:  `error.code`  (e.g. `INVALID_OTP`)
 *   - our backend API errors:        `err.name`    (e.g. `EmailAlreadyRegisteredError`)
 *
 * The banner tries better-auth's code table first, then our API
 * error names, then falls back to a generic message. Overrides for
 * better-auth codes are selective; anything not switched on falls
 * through to `authErrorCodes[code].message` verbatim.
 */

import type { ReactNode } from 'react'

import { Link } from '@/customer/components/ui/link'
import { type AuthErrorCode, authErrorCodes } from '@/customer/lib/auth'
import { routes } from '@/customer/routes'

type ApiErrorName = 'EmailAlreadyRegisteredError' | 'PhoneAlreadyRegisteredError'

function renderBetterAuthError(code: string): ReactNode | null {
  switch (code as AuthErrorCode) {
    case 'USER_ALREADY_EXISTS':
    case 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL':
    case 'PHONE_NUMBER_EXIST':
      return (
        <>
          That email or phone is already in use. <Link to={routes.signIn}>Sign in instead</Link>
        </>
      )
    case 'INVALID_OTP':
      return "That code doesn't match. Try again, or tap resend for a new one."
    case 'OTP_EXPIRED':
      return 'That code has expired. Tap resend to get a new one.'
    case 'TOO_MANY_ATTEMPTS':
      return 'Too many attempts. Please wait a moment before trying again.'
    case 'USER_NOT_FOUND':
      return "We couldn't find an account for that email or phone."
    case 'EMAIL_NOT_VERIFIED':
      return (
        <>
          Your email hasn't been verified.{' '}
          <Link to={routes.account.email.verify}>Verify email</Link>
        </>
      )
    case 'PHONE_NUMBER_NOT_VERIFIED':
      return (
        <>
          Your phone hasn't been verified.{' '}
          <Link to={routes.account.phone.verify}>Verify phone</Link>
        </>
      )
  }
  if (code in authErrorCodes) {
    return authErrorCodes[code as AuthErrorCode].message
  }
  return null
}

function renderApiError(name: string): ReactNode | null {
  switch (name as ApiErrorName) {
    case 'EmailAlreadyRegisteredError':
      return (
        <>
          That email is already in use. <Link to={routes.signIn}>Sign in instead</Link>
        </>
      )
    case 'PhoneAlreadyRegisteredError':
      return (
        <>
          That phone number is already in use. <Link to={routes.signIn}>Sign in instead</Link>
        </>
      )
  }
  return null
}

/**
 * Destructive banner for auth-flow errors.
 *
 * `code` is either a better-auth error code (e.g. `INVALID_OTP`) or a
 * backend `Error.name` (e.g. `EmailAlreadyRegisteredError`). An empty
 * / nullish code renders nothing.
 */
export function AuthErrorBanner({ code }: { code: string | null | undefined }) {
  if (!code) return null
  const content =
    renderBetterAuthError(code) ?? renderApiError(code) ?? 'Something went wrong. Please try again.'
  return (
    <div className="rounded-lg border-2 border-destructive bg-destructive/10 p-4 text-body-md text-destructive">
      {content}
    </div>
  )
}
